import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { smsProgram } from "@/lib/sms-program";
import { requiredDocuments } from "@/lib/required-documents";
import { useGetPublicBookingSeasonQuery } from "@/app/appointments/booking-season/bookingSeasonApi";
import { bookingSeasonGate } from "@/app/booking-flow/bookingSeasonGate";
import { BookingSeasonClosedNotice } from "@/app/booking-flow/components/BookingSeasonClosedNotice";
import { documentsAvailability, useGetPublicBookingDocumentsQuery } from "@/app/document-upload/publicDocumentsApi";

export default function BookingLandingPage() {
  const phone = "+12535172263";
  const formattedPhone = `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;

  // Only New Appointment depends on the booking season; managing an appointment works either way.
  // A failed read still shows the button: it only leads to /booking/new, which re-reads the season and
  // offers a retry rather than the ZIP step if it still cannot (see bookingSeasonGate).
  const season = bookingSeasonGate(useGetPublicBookingSeasonQuery(undefined, { refetchOnMountOrArgChange: true }));

  // Document Upload is a real link only while customer documents are on (AB#774). There is no public
  // flag endpoint, so this reads the documents list's answer (see documentsAvailability): until it is
  // known to be on, the button stays as it was, disabled.
  const documentsOn = documentsAvailability(useGetPublicBookingDocumentsQuery(undefined, { refetchOnMountOrArgChange: true })) === "on";

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column: intro + document checklist */}
          <div className="lg:col-span-2">
            <h1 className="text-2xl font-semibold" style={{ color: "#009681" }}>
              Welcome to your MSC's Appointment Scheduler
            </h1>

            <div className="mt-6 space-y-4 text-red-600">
              <p>ALL appointments are in-person appointments.</p>
              <p>
                  Based on the capacity of our facility and for the safety of our clients and staff, you will have to come 15 minutes before your appointment time to fill out necessary paperwork (BIF). If you arrive late to your appointment, you will not be seen and you will need to reschedule. We recommend you to cancel your existing appointment and schedule another one if you feel unwell. Due to limited lobby space, we prefer that only one person comes to the appointment.
              </p>
            </div>

            <h2 className="mt-8 font-semibold text-slate-900">List of Docs to Submit</h2>
            <p className="mt-2 text-red-600">
              Failure to bring all required <u>printed</u> documentation with you to your appointment will delay your
              assistance. <strong>We require ALL documents listed below:</strong>
            </p>
            {/* The one shared list (AB#598), the same text the confirmation messages and the phone line use. */}
            <ul className="mt-4 space-y-3 list-disc list-inside text-slate-700">
              {requiredDocuments().full.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="my-2 text-slate-700">
              For Self Employed income, please provide business ledgers or business bank statements listing gross income. If you have business expenses, you can choose to accept a standard 50% deduction each month. If the deduction is higher than 50%, we will need actual receipts or bank statements. These must show the description of the expense, the date, and the amount in order for the expense to be deducted from gross income. MSC's energy assistance program cannot deduct everything that can be deducted on taxes. Tax forms are not acceptable as proof of self-employed income or expenses.
            </div>
            <div className="my-2 text-slate-700">
              Please note after reviewing your documents we may request additional information to complete your application.
            </div>
          </div>

          {/* Right column: actions + info */}
          <div className="lg:col-span-1 space-y-4">
            {season.state === "loading" && <Skeleton className="w-full h-14" />}
            {season.state === "closed" && <BookingSeasonClosedNotice variant="inline" message={season.message} />}
            {(season.state === "open" || season.state === "unavailable") && (
              <Button
                asChild
                size="lg"
                className="w-full justify-center text-base h-auto py-4 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
              >
                <Link to="/booking/new">New Appointment</Link>
              </Button>
            )}
            <Button
              asChild
              size="lg"
              className="w-full justify-center text-base h-auto py-4 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
            >
              <Link to="/booking/manage">Manage Existing Appointment</Link>
            </Button>
            <Button
              // Disabled while customer documents are off. This is a soft blocker as a user can navigate to the page by
              // entering URL directly, where they still see the what-to-bring list.
              aria-disabled={documentsOn ? undefined : true}
              tabIndex={documentsOn ? undefined : -1}
              asChild
              size="lg"
              className="w-full justify-center text-base h-auto py-4 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
            >
              <Link to="/booking/document-upload" data-testid="document-upload-button">Document Upload</Link>
            </Button>
            <Button
              // Disabled until this feature is ready. This is a soft blocker as a user can navigate to the page by entering URL directly.
              aria-disabled
              tabIndex={-1}
              asChild
              size="lg"
              className="w-full justify-center text-base h-auto py-4 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
            >
              <Link to="/booking/application-status">Check Application Status</Link>
            </Button>

            <Card>
              <CardHeader>
                <CardTitle>Information</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">
                MSC offers people pathways out of poverty through support and resources in education, employment, housing, energy assistance, and food. MSC has three different service sites.
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Locations of MSC Energy Assistance sites</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">
                <ul className="my-2 list-disc list-inside text-slate-700">
                  <li>Federal Way Office</li>
                  <li>Kent Office</li>
                  <li>Enumclaw Outreach Site</li>
                </ul>
                <div>
                  Based on your zip code and funding availability, you will be automatically assigned to one of the above location.
                </div>
                <div>
                  Within minutes of making your appointment, you should receive a confirmation with your appointment details and the list of documents to bring. In addition, you will also receive a phone reminder the day before your appointment.
                </div>
                <div>
                  Appointments can be canceled or made on this website or our automated phone system by dialing <a href={`tel:${phone}`}>{formattedPhone}</a>. Due to high demand on energy assistance, there may not be appointment available at this time. Please be patient and try again later.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <PublicFooter>
        Multi-Service Center &nbsp;·&nbsp; Energy Assistance &nbsp;·&nbsp;{" "}
        <Link className="underline" to={smsProgram.termsPath}>Text Message Terms</Link> &nbsp;·&nbsp;{" "}
        <Link className="underline" to={smsProgram.privacyPath}>Privacy Policy</Link>
      </PublicFooter>
    </div>
  );
}
